import Auction from '@/models/Auction';
import SavedAuction from '@/models/SavedAuction';
import AuctionClient from '@/api/AuctionClient';
import AuctionsClient from '@/api/AuctionsClient';
import {
  negotiationStates,
  acceptedStates,
} from '@/constants/auctionStates';
import featureFlagSlugs from '@/constants/featureFlagSlugs';
import cloneDeep from 'lodash.clonedeep';

import {
  GET_MARKETPLACE_AUCTIONS_BY_CLIENT_METHOD,
  ASSIGN_COLLECTION_KEYS_TO_AUCTIONS,
  REMOVE_COLLECTION_KEYS_FROM_AUCTIONS,
  SET_AUCTION_VISIBILITY,
  ADD_AUCTIONS,
  REMOVE_AUCTIONS,
  UPDATE_AUCTIONS,
  SYNC_AUCTIONS_DATA,
  GET_AUCTIONS_DATA,
  SUBSCRIBE_MARKETPLACE_AUCTIONS,
  UNSUBSCRIBE_MARKETPLACE_AUCTIONS,
  UPDATE_SELECTED_AUCTION_LANE,
  UPDATE_SELECTED_AUCTION_LANE_SUB_FILTER,
  HANDLE_BID_MESSAGE,
  HANDLE_BID_API_RESPONSE,
  GET_AUCTION_FROM_VUEX_BY_AUCTION_ID,
  GET_AUCTIONS_FROM_VUEX_BY_AUCTION_IDS,
  HANDLE_END_MESSAGE,
  HANDLE_ENDED_UPDATE_MESSAGE,
  UPDATE_AD_HOC_SEARCH_IS_ACTIVE,
  UPDATE_SAVED_SEARCH_IS_ACTIVE,
  CLEAR_AUCTIONS,
  HANDLE_KILLED_MESSAGE,
  UPDATE_HAS_MORE_PAGES,
  UPDATE_AUCTION_LANES_REFRESHING,
  GET_LEGACY_CARFAX_ALERTS_ON_AUCTION,
  LOAD_VIEWING_AUCTION_MODEL,
  UPDATE_VIEWING_AUCTION_MODEL,
  CLEAR_VIEWING_AUCTION_MODEL,
  REFRESH_VIEWING_AUCTION_MODEL,
  UPDATE_LEGACY_AUCTION_RANK_IN_LIST,
  RETAIN_GRAPHQL_SUBSCRIPTION,
  CLEAR_GRAPHQL_SUBSCRIPTIONS,
  HANDLE_GRAPHQL_UPDATE,
} from './marketplace-auctions.actions.type';
import { GET_AUCTION_IDS, GET_SUBSCRIPTION_BY_AUCTION_ID } from './marketplace-auctions.getters.type';
import {
  UPDATE_AUCTIONS as UPDATE_AUCTION_MUTATION,
  UPDATE_SELECTED_AUCTION_LANE as UPDATE_SELECTED_AUCTION_LANE_MUTATION,
  UPDATE_SELECTED_AUCTION_LANE_SUB_FILTER as UPDATE_SELECTED_AUCTION_LANE_SUB_FILTER_MUTATION,
  SET_SYNC_AUCTIONS_DATA_REQUEST as SET_SYNC_AUCTIONS_DATA_REQUEST_MUTATION,
  ADD_AUCTION_IDS_TO_WAITING_FOR_SYNC_COLLECTION,
  CLEAR_AUCTION_IDS_WAITING_FOR_SYNC_COLLECTION,
  UPDATE_AD_HOC_SEARCH_IS_ACTIVE as UPDATE_AD_HOC_SEARCH_IS_ACTIVE_MUTATION,
  UPDATE_SAVED_SEARCH_IS_ACTIVE as UPDATE_SAVED_SEARCH_IS_ACTIVE_MUTATION,
  UPDATE_LAST_SYNC_AUCTIONS_DATA_REQUEST_TIMESTAMP,
  CLEAR_AUCTIONS as CLEAR_AUCTIONS_MUTATION,
  REMOVE_AUCTIONS as REMOVE_AUCTIONS_MUTATION,
  UPDATE_HAS_MORE_PAGES as UPDATE_HAS_MORE_PAGES_MUTATION,
  UPDATE_VIEWING_AUCTION_MODEL as UPDATE_VIEWING_AUCTION_MODEL_MUTATION,
  UPDATE_LEGACY_AUCTION_RANK_IN_LIST as UPDATE_LEGACY_AUCTION_RANK_IN_LIST_MUTATION,
  UPDATE_AUCTION_LANES_REFRESHING as UPDATE_AUCTION_LANES_REFRESHING_MUTATION,
  RETAIN_GRAPHQL_SUBSCRIPTION as RETAIN_GRAPHQL_SUBSCRIPTION_MUTATION,
  REMOVE_GRAPHQL_SUBSCRIPTION_BY_AUCTION_IDS,
} from './marketplace-auctions.mutations.type';

const syncAuctionsDataRequestDebounceInterval = 5000;

function getStartingIndex(auctions, collectionKey) {
  /* eslint-disable-next-line no-unsafe-optional-chaining */
  const currentLength = auctions.filter((auction) => auction
    ?.includeInCollections?.some((a) => a.fetchMethod === collectionKey))?.length;

  return currentLength ? currentLength - 1 : 0;
}

function adjustAuctionStatusPills(
  highBid,
  highBidderUserId,
  highBidderDealerId,
  currentUser,
  { floorPrice, participant, autosell },
  { statusPills },
) {
  const arr = [];

  if (autosell && statusPills?.includes('no_reserve')) {
    // show no reserve if auction is no reserve always
    arr.push('no_reserve');
  } else if (autosell && highBid >= floorPrice) {
    // else show reserve met when reached
    arr.push('reserve_met');
  } else if (statusPills?.includes('near_reserve')) {
    // else show near reserve
    arr.push('near_reserve');
  } else if (statusPills?.includes('low_reserve')) {
    // else show low reserve
    arr.push('low_reserve');
  }

  if (currentUser.isMemberOfWinningDealership(highBidderDealerId)
    || highBidderUserId === currentUser.id) {
    arr.push('high_bidder');
  }

  if (highBidderUserId !== currentUser.id && participant) {
    arr.push('out_bid');
  }

  // show those statuses
  return arr;
}

function isValidViewingAuctionModel(viewingAuctionModel) {
  // a valid viewing auction model has properties and is not a saved auction for syncing purposes
  return Object.keys(viewingAuctionModel).length && !(viewingAuctionModel instanceof SavedAuction);
}

async function isOnSrpRefreshWithGraphQl(rootState, dispatch) {
  const usingGraphQlOnSrp = await dispatch('getFlag', featureFlagSlugs.enableSrpRefresh, { root: true })
  && await dispatch('getFlag', featureFlagSlugs.enableGraphqlOnSrp, { root: true });

  return rootState?.route?.name === 'Marketplace' && usingGraphQlOnSrp;
}

const actions = {
  async [GET_MARKETPLACE_AUCTIONS_BY_CLIENT_METHOD]({
    rootGetters,
    dispatch,
    state,
  }, {
    fetchMethod,
    clearCollectionKeys = false,
    params = [],
    paramsObj = {},
    isRecommendations = false,
  }) {
    const auctionsClient = new AuctionsClient(rootGetters.currentUser);
    if (typeof auctionsClient[fetchMethod] !== 'function') {
      if (import.meta.env.DEV) {
        window.console.error(`Fetch Method "${fetchMethod}" is not defined on AuctionsClient. Aborting.`);
      }
      return false;
    }

    let response;
    if (Object.keys(paramsObj).length) {
      response = await auctionsClient[fetchMethod](paramsObj);
    } else {
      response = await auctionsClient[fetchMethod](...params);
    }

    const {
      results: auctions,
    } = response;
    let auctionsWithCollectionKeys = [...auctions];
    if (auctions.length > 0) {
      const auctionIds = auctions.map((a) => a.id);
      const existingAuctions = await dispatch(
        GET_AUCTIONS_FROM_VUEX_BY_AUCTION_IDS,
        { auctionIds },
      );

      const updatedAuctions = auctions.map((auction) => {
        const existingAuction = existingAuctions.find((ea) => ea.id === auction.id);
        if (existingAuction && !clearCollectionKeys) {
          return {
            ...existingAuction,
            ...auction,
          };
        }

        return auction;
      });

      const collectionKeyToStore = isRecommendations
        ? [`${fetchMethod}Recommended`]
        : [fetchMethod];

      // Assign the received auctions to their specific collection key
      auctionsWithCollectionKeys = await dispatch(
        ASSIGN_COLLECTION_KEYS_TO_AUCTIONS,
        {
          auctions: updatedAuctions,
          collectionKeys: collectionKeyToStore,
        },
      );

      // Subscribe to those received auctions
      await dispatch(SUBSCRIBE_MARKETPLACE_AUCTIONS, {
        auctions: auctionsWithCollectionKeys,
      });

      /**
       * If these retained auctions are active and the user is a participant,
       * Add those auctions to the collection of active buying auctions in the fast action panel
       *
       * This assignment is done separately instead of
       * being included in the collectionKeys of the first assignment.
       *
       * This prevents the live lane from showing a partial auction list of
       * only participated auctions with a high end time
       * and not rendering auctions that were not partipated with of a lower end time
      */
      const activeParticipantAuctions = state.auctions
        .filter((a) => a.participant && a.status === 'active');

      if (activeParticipantAuctions.length) {
        // We found active auctions the user is participating in, add them to active buying
        await dispatch(
          ASSIGN_COLLECTION_KEYS_TO_AUCTIONS,
          {
            auctions: activeParticipantAuctions,
            collectionKeys: ['getMyActiveAuctions'],
          },
        );
      }
    }

    return {
      ...response,
      results: [
        ...auctionsWithCollectionKeys,
      ],
    };
  },
  [ASSIGN_COLLECTION_KEYS_TO_AUCTIONS](
    {
      commit,
      state,
    },
    { auctions, collectionKeys = [] },
  ) {
    let auctionCollectionsArray = [];

    const selectedLaneFetchMethod = state.selectedAuctionLane?.fetchMethod;
    const selectedLaneAdjustIndexes = state.selectedAuctionLane?.adjustIndexes;

    collectionKeys.forEach((c) => {
      // Determine where in the state auctions array to place the new auctions being assigned
      let indexToStartFrom = getStartingIndex(state.auctions, c);

      if (state.auctions.length) {
        // auctions to be added in should be one plus the index to start from
        indexToStartFrom += 1;
      }

      // For each collection key provided, setup the placement where the auction would be placed
      // relative to that collection
      auctionCollectionsArray = [
        ...auctionCollectionsArray,
        ...auctions.map((auction, index) => ({
          auctionId: auction.id,
          index: indexToStartFrom + index,
          fetchMethod: c,
          endTime: auction.endTime,
        })),
      ];
    });

    const updatedAuctions = auctions.map((auction) => {
      /*
      * If the indexes should be shifted due to insertions by the end time of new auctions
      * within the listing, then grab the auction where this new auction would be placed beside
      * and set the new auction collection index to be the same index of the auction it is being
      * placed beside
      */
      if (selectedLaneAdjustIndexes
        && selectedLaneFetchMethod
        && state.auctions.length
      ) {
        const relevantCollection = state.auctions.reduce((arr, a) => {
          if ((a?.includeInCollections || []).length) {
            const foundCollection = a.includeInCollections
              .find((c) => c.fetchMethod === selectedLaneFetchMethod);
            if (foundCollection) {
              arr.push(foundCollection);
            }
          }
          return arr;
        }, []);

        // find the first occurrence of a collection to compare from the sorted collections
        const collectionToCompare = relevantCollection
          .sort((a, b) => a.endTime - b.endTime)
          .find((c) => auction.endTime < c.endTime);

        if (collectionToCompare) {
          auctionCollectionsArray.forEach((c) => {
            const collectionToAdjust = c;
            if (collectionToAdjust.auctionId === auction.id
              && c?.fetchMethod === selectedLaneFetchMethod) {
              // the adjusted auction should appear above the found auction index
              // since the adjusted auction ends before the found auction
              collectionToAdjust.index = collectionToCompare.index - 1;
            }
          });
        }
      }

      // Include the collections for each auction to be sent off to the mutations
      const collectionsForAuction = auctionCollectionsArray
        .filter((a) => a.auctionId === auction.id)
        .map((a) => ({
          fetchMethod: a.fetchMethod,
          index: a.index,
          endTime: a.endTime,
        }));

      const existingCollections = [...auction?.includeInCollections || []];

      // If there was existing collections for the auction, include collections except the
      // ones already mapped
      if (existingCollections.length) {
        const mappedFetchMethods = collectionsForAuction.map((c) => c.fetchMethod);
        existingCollections.forEach((e) => {
          if (!mappedFetchMethods.includes(e.fetchMethod)) {
            collectionsForAuction.push(e);
          }
        });
      }

      return {
        ...auction,
        includeInCollections: [
          ...collectionsForAuction,
        ],
      };
    });
    commit(UPDATE_AUCTION_MUTATION, updatedAuctions);

    return updatedAuctions;
  },
  [REMOVE_COLLECTION_KEYS_FROM_AUCTIONS](
    {
      commit,
    },
    { auctions, collectionKeys = [] },
  ) {
    const updatedAuctions = auctions.map((auction) => ({
      ...auction,
      includeInCollections: [...new Set([
        ...auction.includeInCollections
          .filter((a) => !collectionKeys.includes(a.fetchMethod))
        || [],
      ])],
    }));
    const auctionsToRemove = updatedAuctions.filter((a) => a.includeInCollections.length === 0);
    const auctionsToUpdate = updatedAuctions.filter((a) => a.includeInCollections.length > 0);
    commit(REMOVE_AUCTIONS_MUTATION, auctionsToRemove);
    commit(UPDATE_AUCTION_MUTATION, auctionsToUpdate);

    return {
      removedAuctions: auctionsToRemove,
      updatedAuctions: auctionsToUpdate,
    };
  },
  [SET_AUCTION_VISIBILITY](
    {
      commit,
      state,
      getters,
    },
    { auctionId, visible },
  ) {
    if (getters[GET_AUCTION_IDS].includes(auctionId)) {
      const existingAuction = state.auctions.find((a) => a.id === auctionId);
      if (existingAuction) {
        const updatedAuction = {
          ...existingAuction,
          visible,
        };
        commit(UPDATE_AUCTION_MUTATION, [updatedAuction]);
        return updatedAuction;
      }
    }
    return null;
  },
  [ADD_AUCTIONS](
    {
      commit,
      dispatch,
    },
    { auctions },
  ) {
    commit(ADD_AUCTIONS, auctions);
    dispatch(SUBSCRIBE_MARKETPLACE_AUCTIONS, { auctions });
  },
  async [REMOVE_AUCTIONS](
    {
      commit,
      dispatch,
    },
    { auctions },
  ) {
    await dispatch(UNSUBSCRIBE_MARKETPLACE_AUCTIONS, { auctions });
    commit(REMOVE_AUCTIONS, auctions);
  },
  [UPDATE_AUCTIONS](
    {
      commit,
    },
    { auctions },
  ) {
    commit(UPDATE_AUCTION_MUTATION, auctions);
  },
  async [CLEAR_AUCTIONS](
    {
      commit,
      dispatch,
      state,
    },
  ) {
    await dispatch(UNSUBSCRIBE_MARKETPLACE_AUCTIONS, { auctions: state.auctions, all: true });
    commit(CLEAR_AUCTIONS_MUTATION);
    commit(CLEAR_AUCTION_IDS_WAITING_FOR_SYNC_COLLECTION);
  },
  [UPDATE_HAS_MORE_PAGES]({
    commit,
  }, {
    hasMorePages,
  }) {
    commit(UPDATE_HAS_MORE_PAGES_MUTATION, hasMorePages);
  },
  [UPDATE_AUCTION_LANES_REFRESHING]({
    commit,
  }, {
    refreshing,
  }) {
    commit(UPDATE_AUCTION_LANES_REFRESHING_MUTATION, refreshing);
  },
  async [GET_LEGACY_CARFAX_ALERTS_ON_AUCTION](
    /*
    * Get the legacy carfax alerts from the vin and auction id
    */
    {
      dispatch,
      rootGetters,
    },
    { vin, id },
  ) {
    let carfaxAlerts;
    try {
      const dealerId = rootGetters.currentUser.defaultDealerId;
      carfaxAlerts = await dispatch('auctions/getCarfaxReport', {
        vin,
        auctionId: id,
        dealerId,
      }, { root: true });
    } catch (carfaxException) {
      // TODO - Do we want to error or not? - ETC
      // await dispatch('auctions/setError', {
      //   message: 'There was a problem retrieving the Carfax Report. Please try again later.',
      // });
    }
    return Promise.resolve(carfaxAlerts?.alerts);
  },
  [SYNC_AUCTIONS_DATA](
    {
      commit,
      dispatch,
      state,
      rootGetters: {
        isAuthorized,
      },
    },
    { auctionIds, retries = 3, force = false },
  ) {
    if (isAuthorized) {
      const timeDifference = ((Date.now()) - state.lastSyncAuctionsDataRequestTimestamp.getTime());
      const intervalElapsed = timeDifference >= syncAuctionsDataRequestDebounceInterval;
      if (intervalElapsed || force) {
        if (state.syncAuctionsDataRequest === null) {
          const { auctionIdsWaitingToSync } = state;
          const auctionIdsToUpdate = [...new Set([
            ...auctionIdsWaitingToSync,
            ...auctionIds,
          ])];

          // whenever a sync operation happens from any updates,
          // update the viewing auction model if its there
          if (isValidViewingAuctionModel(state?.viewingAuctionModel)) {
            if (!auctionIdsToUpdate.includes(state.viewingAuctionModel.id)) {
              auctionIdsToUpdate.push(state.viewingAuctionModel.id);
            }
          }

          const existingAuctionIdsWaitingToSync = state.auctions
            .filter((a) => auctionIdsToUpdate.includes(a.id))
            .map((b) => b.id);
          if (existingAuctionIdsWaitingToSync.length > 0) {
            commit(UPDATE_LAST_SYNC_AUCTIONS_DATA_REQUEST_TIMESTAMP, new Date());
            const syncAuctionsDataRequest = dispatch(
              GET_AUCTIONS_DATA,
              {
                auctionIds: existingAuctionIdsWaitingToSync,
                retries,
              },
            ).then(({ results: auctions }) => {
              commit(CLEAR_AUCTION_IDS_WAITING_FOR_SYNC_COLLECTION);
              commit(UPDATE_AUCTION_MUTATION, auctions);
              if (isValidViewingAuctionModel(state?.viewingAuctionModel)) {
                dispatch(REFRESH_VIEWING_AUCTION_MODEL);
              }
            }).catch(() => {
              // on err catch, retain those ids to be re-synced
              commit(ADD_AUCTION_IDS_TO_WAITING_FOR_SYNC_COLLECTION, auctionIds);
            }).finally(() => {
              // when request finishes successfully or in error, allow subsequent requests to happen
              commit(SET_SYNC_AUCTIONS_DATA_REQUEST_MUTATION, null);
            });

            // acknowledge that a request is happening
            commit(SET_SYNC_AUCTIONS_DATA_REQUEST_MUTATION, syncAuctionsDataRequest);
          }
        } else {
          commit(ADD_AUCTION_IDS_TO_WAITING_FOR_SYNC_COLLECTION, auctionIds);
        }
      } else {
        commit(ADD_AUCTION_IDS_TO_WAITING_FOR_SYNC_COLLECTION, auctionIds);
      }
    }
  },
  async [GET_AUCTIONS_DATA](
    {
      rootGetters,
    },
    { auctionIds, retries = 3 },
  ) {
    const auctionsClient = new AuctionsClient(rootGetters.currentUser);
    const response = await auctionsClient.getAuctionsById(auctionIds, retries);
    return response;
  },
  async [SUBSCRIBE_MARKETPLACE_AUCTIONS]({
    rootState,
    dispatch,
    getters,
    rootGetters,
  }, { auctions }) {
    if (await isOnSrpRefreshWithGraphQl(rootState, dispatch)) {
      const auctionsClient = new AuctionsClient(rootGetters.currentUser);

      auctions.forEach((a) => {
        // create a subscription per auction if not already being tracked
        if (!getters[GET_SUBSCRIPTION_BY_AUCTION_ID](a.id)) {
          auctionsClient.subscribeAuctionsByIds({
            auctionIds: [a.id],
            callbackFn: (data) => {
              if (Object.keys(data.auctionLiveUpdates).length) {
                dispatch(HANDLE_GRAPHQL_UPDATE, { auction: data.auctionLiveUpdates });
              }
            },
            handleSubscriptionFn: (trackedSubscription) => {
              dispatch(RETAIN_GRAPHQL_SUBSCRIPTION, {
                subscription: {
                  ...trackedSubscription,
                },
              });
            },
          });
        }
      });
    } else {
      const activeAuctionChannelKeys = auctions.map((auction) => `updates.${auction.id}`);
      const endedAuctionChannelKeys = auctions
        .map((auction) => `ended_updates.${auction.id}`);
      await dispatch('subscribeToChannels', [...activeAuctionChannelKeys, ...endedAuctionChannelKeys], { root: true });
    }
  },
  async [UNSUBSCRIBE_MARKETPLACE_AUCTIONS]({
    rootState,
    dispatch,
  }, { auctions, all = false }) {
    if (await isOnSrpRefreshWithGraphQl(rootState, dispatch)) {
      dispatch(CLEAR_GRAPHQL_SUBSCRIPTIONS, { auctions, all });
    } else {
      const activeAuctionChannelKeys = auctions.map((auction) => `updates.${auction.id}`);
      const endedAuctionChannelKeys = auctions
        .map((auction) => `ended_updates.${auction.id}`);
      await dispatch('unsubscribeToChannels', [...activeAuctionChannelKeys, ...endedAuctionChannelKeys], { root: true });
    }
  },
  [UPDATE_SELECTED_AUCTION_LANE](
    {
      commit,
    },
    selectedAuctionLane,
  ) {
    commit(UPDATE_SELECTED_AUCTION_LANE_MUTATION, selectedAuctionLane);
  },
  [UPDATE_SELECTED_AUCTION_LANE_SUB_FILTER](
    {
      commit,
    },
    selectedAuctionLaneSubFilter,
  ) {
    commit(UPDATE_SELECTED_AUCTION_LANE_SUB_FILTER_MUTATION, selectedAuctionLaneSubFilter);
  },
  [LOAD_VIEWING_AUCTION_MODEL](
    /*
    * Load both the easypass auction and the legacy auction requests
    * Then update auction the easy pass version and the legacy version
    */
    {
      dispatch,
      rootGetters,
    },
    { auctionId },
  ) {
    const auctionClient = new AuctionClient(rootGetters.currentUser);
    const promises = [
      dispatch(GET_AUCTIONS_DATA, { auctionIds: [auctionId] }), // easypass
      auctionClient.get(auctionId), // legacy
    ];
    return new Promise((resolve, reject) => {
      Promise.all(promises).then(([
        { results: [easyPassViewingAuction] },
        { data: legacyViewingAuction },
      ]) => {
        // update easypass auctions with the easypass response
        dispatch(UPDATE_AUCTIONS, { auctions: [easyPassViewingAuction] });
        // grab the carfax alerts and update the viewing auction with the legacy response
        dispatch(GET_LEGACY_CARFAX_ALERTS_ON_AUCTION, {
          vin: legacyViewingAuction.vin,
          id: legacyViewingAuction.id,
        }).then((carfaxAlerts) => {
          const modifiedLegacyViewingAuction = legacyViewingAuction;

          modifiedLegacyViewingAuction.carfax_alerts = carfaxAlerts;
          dispatch(UPDATE_VIEWING_AUCTION_MODEL, { auction: modifiedLegacyViewingAuction })
            .then(() => resolve());
        });
      }).catch((e) => {
        reject(e);
      });
    });
  },
  [UPDATE_VIEWING_AUCTION_MODEL](
    /*
    * Takes an auction object to create an Auction model class
    * Updates the viewing auction model
    */
    {
      commit,
      state,
      rootGetters,
    },
    { auction, saved = false },
  ) {
    let auctionModel;
    if (saved || state?.viewingAuctionModel instanceof SavedAuction) {
      auctionModel = new SavedAuction(auction, rootGetters.currentUser);
    } else {
      auctionModel = new Auction(auction, rootGetters.currentUser);
    }

    // if the viewing auction exists, either regular or saved and
    // the auction received does not have any carfax alerts,
    // set the carfax alerts that were initially loaded
    if (Object.keys(state?.viewingAuctionModel).length) {
      if (auctionModel.id === state.viewingAuctionModel.id
        && (!auctionModel.carfaxAlerts || !Object.keys(auctionModel.carfaxAlerts).length)
      ) {
        auctionModel.carfaxAlerts = state.viewingAuctionModel.carfaxAlerts;
      }
    }
    commit(UPDATE_VIEWING_AUCTION_MODEL_MUTATION, auctionModel);
  },
  async [CLEAR_VIEWING_AUCTION_MODEL](
    /*
    * Clear the viewing auction and dispatch to unsubscribe channels
    */
    {
      dispatch,
      commit,
      state,
    },
  ) {
    if (!state.auctions.find((a) => a.id === state.viewingAuctionModel?.auction?.id)) {
      await dispatch('auctions/unsubscribeAuctions', 'marketplaceAuctions', { root: true });
    }

    commit(UPDATE_VIEWING_AUCTION_MODEL_MUTATION, {});
    await dispatch(UPDATE_LEGACY_AUCTION_RANK_IN_LIST, { rankInList: null });
  },
  async [REFRESH_VIEWING_AUCTION_MODEL](
    /*
    * Refreshes the existing viewing auction and updates in state
    */
    {
      dispatch,
      rootGetters,
      state,
    },
  ) {
    const auctionClient = new AuctionClient(rootGetters.currentUser);
    const response = await auctionClient.get(state.viewingAuctionModel.id);
    const auctionResponseToUpdate = response.data;

    await dispatch(UPDATE_VIEWING_AUCTION_MODEL, { auction: auctionResponseToUpdate });
  },
  [UPDATE_LEGACY_AUCTION_RANK_IN_LIST](
    {
      commit,
    },
    { rankInList },
  ) {
    commit(UPDATE_LEGACY_AUCTION_RANK_IN_LIST_MUTATION, rankInList);
  },
  [UPDATE_AD_HOC_SEARCH_IS_ACTIVE](
    {
      commit,
    },
    { adHocSearchIsActive },
  ) {
    commit(UPDATE_AD_HOC_SEARCH_IS_ACTIVE_MUTATION, adHocSearchIsActive);
  },
  [UPDATE_SAVED_SEARCH_IS_ACTIVE](
    {
      commit,
    },
    { savedSearchIsActive },
  ) {
    commit(UPDATE_SAVED_SEARCH_IS_ACTIVE_MUTATION, savedSearchIsActive);
  },
  [GET_AUCTION_FROM_VUEX_BY_AUCTION_ID](
    {
      state,
    },
    { auctionId },
  ) {
    return state.auctions.find((auction) => auction.id === auctionId);
  },
  [GET_AUCTIONS_FROM_VUEX_BY_AUCTION_IDS](
    {
      state,
    },
    { auctionIds },
  ) {
    return state.auctions.filter((auction) => auctionIds.includes(auction.id));
  },
  async [HANDLE_BID_MESSAGE](
    {
      dispatch,
      state,
      rootGetters,
    },
    pubNubMessage,
  ) {
    const {
      /*
       * Leaving unused fields commented out in the event we need them in the future - ETC
       */
      auction: {
        // bid_count,
        current_bid: highBid,
        end_time: endTime,
        high_bidder_dealer_id: highBidderDealerId,
        high_bidder_id: highBidderUserId,
        id: auctionId,
        // seller_dealer_id,
        // seller_id,
        state: status,
        // unique_bidders,
      },
    } = pubNubMessage;
    const existingAuction = state.auctions.find((auction) => auction.id === auctionId);
    if (existingAuction) {
      const { currentUser } = rootGetters;
      const updatedValues = {
        buyerInfo: {
          dealerId: highBidderDealerId,
          userId: highBidderUserId,
          highBid,
        },
        participant: existingAuction.participant || highBidderUserId === currentUser.id,
        canBid: highBidderUserId !== currentUser.id && status === 'active',
        statusPills: [],
        endTime,
        highBid,
        isMemberOfTopBiddingDealership: currentUser.isMemberOfWinningDealership(highBidderDealerId),
        isPrimaryTopBidder: highBidderUserId === currentUser.id,
      };
      const updatedAuction = {
        ...existingAuction,
        ...updatedValues,
      };

      updatedAuction.statusPills = adjustAuctionStatusPills(
        highBid,
        highBidderUserId,
        highBidderDealerId,
        currentUser,
        updatedAuction,
        existingAuction,
      );

      let auctionsWithCollectionKeys = [
        updatedAuction,
      ];

      if (updatedValues.participant) {
        auctionsWithCollectionKeys = await dispatch(
          ASSIGN_COLLECTION_KEYS_TO_AUCTIONS,
          {
            auctions: [updatedAuction],
            collectionKeys: ['getMyActiveAuctions'],
          },
        );
      }
      await dispatch(UPDATE_AUCTIONS, { auctions: auctionsWithCollectionKeys });
      await dispatch(SYNC_AUCTIONS_DATA, { auctionIds: [updatedAuction.id] });
    }
  },
  async [HANDLE_END_MESSAGE](
    {
      dispatch,
      state,
      rootGetters,
    },
    pubNubMessage,
  ) {
    const {
      auction: {
        current_bid: highBid,
        high_bidder_dealer_id: highBidderDealerId,
        high_bidder_id: highBidderUserId,
        id: auctionId,
        end_time: endTime,
        state: status,
      },
    } = pubNubMessage;
    const existingAuction = state.auctions.find((auction) => auction.id === auctionId);
    if (existingAuction) {
      const { currentUser } = rootGetters;
      const updatedValues = {
        buyerInfo: {
          dealerId: highBidderDealerId,
          userId: highBidderUserId,
          highBid,
        },
        endTime,
        status,
        participant: existingAuction.participant || highBidderUserId === currentUser.id,
        isMemberOfTopBiddingDealership: currentUser.isMemberOfWinningDealership(highBidderDealerId),
        isPrimaryTopBidder: highBidderUserId === currentUser.id,
      };
      const updatedAuction = {
        ...existingAuction,
        ...updatedValues,
      };
      let auctionsWithCollectionKeys = [
        updatedAuction,
      ];
      if (updatedAuction.participant) {
        const { updatedAuctions: updatedAuctionsWithKeys } = await dispatch(
          REMOVE_COLLECTION_KEYS_FROM_AUCTIONS,
          {
            auctions: [updatedAuction],
            collectionKeys: ['getMyActiveAuctions'],
          },
        );
        auctionsWithCollectionKeys = updatedAuctionsWithKeys;
        const inNegotiationState = negotiationStates.includes(updatedAuction.status);
        const inAcceptedState = acceptedStates.includes(updatedAuction.status);
        let collectionKeys = [];
        if (inNegotiationState) {
          collectionKeys = ['getMyNegotiations'];
        } else if (inAcceptedState) {
          collectionKeys = ['getMyNeedsFinalizing'];
        }

        auctionsWithCollectionKeys = await dispatch(
          ASSIGN_COLLECTION_KEYS_TO_AUCTIONS,
          {
            auctions: auctionsWithCollectionKeys,
            collectionKeys,
          },
        );
      }
      await dispatch(UPDATE_AUCTIONS, { auctions: auctionsWithCollectionKeys });
      await dispatch(SYNC_AUCTIONS_DATA, { auctionIds: [updatedAuction.id] });
    } else if (isValidViewingAuctionModel(state?.viewingAuctionModel)) {
      await dispatch(REFRESH_VIEWING_AUCTION_MODEL);
    }
  },
  async [HANDLE_ENDED_UPDATE_MESSAGE](
    {
      dispatch,
      state,
    },
    pubNubMessage,
  ) {
    const {
      auction: {
        id: unparsedAuctionId,
        bid_amount: bidAmount,
      },
    } = pubNubMessage;

    if (typeof bidAmount === 'undefined' || bidAmount === null) {
      const auctionId = parseInt(unparsedAuctionId, 10);
      const existingAuction = state.auctions.find((auction) => auction.id === auctionId);
      if (existingAuction) {
        await dispatch(SYNC_AUCTIONS_DATA, { auctionIds: [auctionId] });
      } else if (isValidViewingAuctionModel(state?.viewingAuctionModel)) {
        await dispatch(REFRESH_VIEWING_AUCTION_MODEL);
      }
    }
  },
  async [HANDLE_KILLED_MESSAGE](
    {
      dispatch,
      state,
    },
    pubNubMessage,
  ) {
    const {
      auction: {
        // current_bid: currentBid,
        end_time: endTime,
        // high_bidder_dealer_id: highBidderDealerId,
        // high_bidder_id: highBidUserId,
        id: auctionId,
        // seller_dealer_id: sellerDealerId,
        // seller_id: sellerUserId,
        state: status,
        // unique_bidders: uniqueBidders,
      },
    } = pubNubMessage;
    const existingAuction = state.auctions.find((auction) => auction.id === auctionId);
    if (existingAuction) {
      const updatedAuction = {
        ...existingAuction,
        ...{
          status,
          endTime,
        },
      };
      await dispatch(UPDATE_AUCTIONS, { auctions: [updatedAuction] });
    }

    if (isValidViewingAuctionModel(state?.viewingAuctionModel)) {
      await dispatch(REFRESH_VIEWING_AUCTION_MODEL);
    }
  },
  async [HANDLE_BID_API_RESPONSE](
    {
      dispatch,
      state,
      rootGetters,
    },
    auctionBidResponse,
  ) {
    const {
      auctionId,
      auction: {
        highBidInfo: {
          amount: highBid,
          userId: highBidderUserId,
          dealerId: highBidderDealerId,
        },
        auctionStatus: status,
        endTime,
        participant,
      },
    } = auctionBidResponse;
    const existingAuction = state.auctions.find((auction) => auction.id === auctionId);
    if (existingAuction) {
      const { currentUser } = rootGetters;
      const updatedValues = {
        buyerInfo: {
          dealerId: highBidderDealerId,
          userId: highBidderUserId,
          highBid,
        },
        endTime,
        participant,
        statusPills: [],
        status,
        canBid: highBidderUserId !== currentUser.id && status === 'active',
        highBid,
        isMemberOfTopBiddingDealership: currentUser.isMemberOfWinningDealership(highBidderDealerId),
        isPrimaryTopBidder: highBidderUserId === currentUser.id,
      };

      const updatedAuction = {
        ...existingAuction,
        ...updatedValues,
      };

      updatedAuction.statusPills = adjustAuctionStatusPills(
        highBid,
        highBidderUserId,
        highBidderDealerId,
        currentUser,
        updatedAuction,
        existingAuction,
      );

      if (updatedValues.participant) {
        const auctionsWithCollectionKeys = await dispatch(
          ASSIGN_COLLECTION_KEYS_TO_AUCTIONS,
          {
            auctions: [updatedAuction],
            collectionKeys: ['getMyActiveAuctions'],
          },
        );
        await dispatch(UPDATE_AUCTIONS, { auctions: auctionsWithCollectionKeys });
        await dispatch(SYNC_AUCTIONS_DATA, { auctionIds: [updatedAuction.id] });
      }
    }
  },

  [RETAIN_GRAPHQL_SUBSCRIPTION](
    {
      getters,
      commit,
    },
    { subscription = {} },
  ) {
    const auctionId = subscription.variables.auction_ids?.[0];

    if (!(getters[GET_SUBSCRIPTION_BY_AUCTION_ID](auctionId))) {
      commit(RETAIN_GRAPHQL_SUBSCRIPTION_MUTATION, subscription);
    }
  },

  [CLEAR_GRAPHQL_SUBSCRIPTIONS](
    {
      state,
      commit,
      rootGetters,
    },
    { auctions = [], all = false },
  ) {
    const auctionsClient = new AuctionsClient(rootGetters.currentUser);
    let subscriptionIdsToRemove = [];
    let auctionIdsToRemove = [];

    if (all) {
      // remove all subscriptions from main listing and fast action panel
      subscriptionIdsToRemove = Object.values(state.auctionSubscriptions ?? {})
        .map((s) => s.id);
      auctionIdsToRemove = auctions.map((a) => a.id);
    } else if (auctions.length) {
      auctions.forEach((a) => {
        // remove subscriptions from a provided fetch method
        const originatingSubscription = state.auctionSubscriptions?.[a.id];

        if (originatingSubscription
          && !subscriptionIdsToRemove.includes(originatingSubscription.id)
        ) {
          subscriptionIdsToRemove.push(originatingSubscription.id);
          auctionIdsToRemove.push(a.id);
        }
      });
    }

    subscriptionIdsToRemove.forEach((id) => {
      auctionsClient.unsubscribeAuctionSubscription({ subscriptionId: id });
    });

    commit(REMOVE_GRAPHQL_SUBSCRIPTION_BY_AUCTION_IDS, auctionIdsToRemove);
  },

  async [HANDLE_GRAPHQL_UPDATE](
    {
      dispatch,
      state,
    },
    { auction = {} },
  ) {
    const existingAuction = state.auctions.find((a) => a.id === auction.id);

    if (existingAuction) {
      const updatedAuction = cloneDeep({
        ...existingAuction,
        ...auction,
      });

      const dynamicCollectionKeys = [
        'getMyNeedsFinalizing',
        'getMyNegotiations',
        'getMyPendingProxies',
        'getMyActiveAuctions',
        'getMyWatchlist',
      ];

      const collectionKeysToInclude = [];

      // get the collection keys to be included based on status pills
      if (updatedAuction.statusPills.includes('finalize')) {
        collectionKeysToInclude.push('getMyNeedsFinalizing');
      } else if (
        updatedAuction.statusPills.includes('received_last_negotiation')
        || updatedAuction.statusPills.includes('sent_last_negotiation')
      ) {
        collectionKeysToInclude.push('getMyNegotiations');
      } else if (updatedAuction.statusPills.includes('proxy_scheduled')) {
        collectionKeysToInclude.push('getMyPendingProxies');
      } else if (
        updatedAuction.statusPills.includes('high_bidder')
        || updatedAuction.statusPills.includes('out_bid')
      ) {
        collectionKeysToInclude.push('getMyActiveAuctions');
      } else if (updatedAuction.isOnWatchlist) {
        collectionKeysToInclude.push('getMyWatchlist');
      }

      let auctionsWithCollectionKeys = [updatedAuction];

      if (collectionKeysToInclude.length) {
        // remove the fast action panel collection keys
        updatedAuction.includeInCollections = updatedAuction.includeInCollections
          .filter((c) => !dynamicCollectionKeys.includes(c.fetchMethod));

        // include the fast action panel collection keys that were re-assessed
        auctionsWithCollectionKeys = await dispatch(
          ASSIGN_COLLECTION_KEYS_TO_AUCTIONS,
          {
            auctions: [updatedAuction],
            collectionKeys: collectionKeysToInclude,
          },
        );
      }

      // updates auction along with main and updated fast action panel collection keys
      await dispatch(UPDATE_AUCTIONS, { auctions: auctionsWithCollectionKeys });

      if (isValidViewingAuctionModel(state?.viewingAuctionModel)
        && state?.viewingAuctionModel.id === auction.id
      ) {
        await dispatch(REFRESH_VIEWING_AUCTION_MODEL);
      }
    }
  },
};

export default actions;
